import { Text, View } from "@react-pdf/renderer";
import dayjs from "dayjs";

import amlReportStyles from "../Styles/amlReportStyles";
import theme from "../../../css/theme";
import pepSanPdfStyles from "../Styles/pepSanPdfStyles";

import {
  TYPE_LABELS,
  TYPE_DISPLAY_ORDER,
  MY_CLIENT_REASON,
} from "../../CheckDetails/Utils/getConstants";

import { getDecisionForService } from "../../CheckDetails/Utils/Experian/getServiceDecision";
import experianServiceNames from "../../CheckDetails/Utils/Experian/getExperianServiceNames";
import capitalizeFirst from "../../Checks/utils/factories/capitalise";
import getIconAndColorAndDecision from "../Utils/getIconColorDecision";
import {
  anyMatchIsMyClient,
  getAllMatchesResolved,
} from "../../CheckDetails/Utils/Experian/allMatchesHaveReasons";
import MetaData from "./getMetaData";
import Banner from "../../Components/Banners/Banner";

// ---------------- helpers (match UI) ----------------
const normTxt = (v) => (v ?? "").toString().trim().toLowerCase();
const safe = (v, fb = "-") =>
  v === null || v === undefined || v === "" ? fb : v;

const fmtDate = (v) => {
  if (!v) return "-";
  const d = dayjs(v);
  return d.isValid() ? d.format("DD MMM YYYY") : String(v);
};

const criteriaForType = (match, type) => {
  const list = Array.isArray(match?.matchingCriteria)
    ? match.matchingCriteria
    : [];
  return list.filter((c) => String(c?.searchType || "").toUpperCase() === type);
};

const pickPrimaryName = (names = []) => {
  if (!Array.isArray(names) || !names.length) return null;
  return (
    names.find((n) =>
      String(n?.type || "")
        .toLowerCase()
        .includes("primary"),
    ) || names[0]
  );
};

const joinName = (n) => {
  if (!n) return "-";
  return `${safe(n?.firstName, "")} ${safe(n?.surname, "")}`.trim() || "-";
};

const stringifyRegionList = (criteria) => {
  const regions = criteria.map((c) => c?.region).filter(Boolean);
  return regions.length ? regions.join(", ") : "-";
};

const pickPepSanElement = (response) => {
  const payload = response?.clientResponsePayload;
  const els = payload?.decisionElements || [];
  const aliases = Array.isArray(experianServiceNames.PEP_SANCTIONS)
    ? experianServiceNames.PEP_SANCTIONS
    : [experianServiceNames.PEP_SANCTIONS];

  return els.find((d) =>
    aliases.some((s) => normTxt(s) === normTxt(d?.serviceName)),
  );
};

const uniq = (arr) => [...new Set((arr || []).filter(Boolean))];

const getCriteriaExtras = (criteria = []) => {
  const regions = uniq(criteria.map((c) => c?.region));
  const relationships = uniq(criteria.map((c) => c?.relationship));
  const relatedToIds = uniq(criteria.map((c) => c?.relatedToId));

  return {
    regionsText: regions.length ? regions.join(", ") : "-",
    relationshipsText: relationships.length ? relationships.join(", ") : "-",
    relatedToIdsText: relatedToIds.length ? relatedToIds.join(", ") : "-",
  };
};

const inferAvailableTypes = (element) => {
  const fromMatches = Array.isArray(element?.matches)
    ? element.matches
        .map((m) => String(m?.name || "").toUpperCase())
        .filter(Boolean)
    : [];
  if (fromMatches.length) return [...new Set(fromMatches)];

  const results =
    element?.otherData?.response?.searchResults?.[0]?.results || [];
  const inferred = [];
  results.forEach((r) => {
    (r?.matchingCriteria || []).forEach((c) => {
      const t = String(c?.searchType || "").toUpperCase();
      if (t) inferred.push(t);
    });
  });

  const uniq = [...new Set(inferred)];
  return uniq.length ? uniq : ["SAN", "PEP", "RCA"];
};

const sortTypes = (types) => {
  const list = [...types];
  return list.sort((a, b) => {
    const ia = TYPE_DISPLAY_ORDER.indexOf(a);
    const ib = TYPE_DISPLAY_ORDER.indexOf(b);
    if (ia === -1 && ib === -1) return a.localeCompare(b);
    if (ia === -1) return 1;
    if (ib === -1) return -1;
    return ia - ib;
  });
};

// UI ref scheme: SAN1, PEP2, ...
const buildCards = (results) => {
  const counters = {};
  const out = [];

  results.forEach((match) => {
    const types = Array.isArray(match?.matchingCriteria)
      ? [
          ...new Set(
            match.matchingCriteria.map((c) =>
              String(c?.searchType || "").toUpperCase(),
            ),
          ),
        ]
      : [];

    types.forEach((t) => {
      counters[t] = (counters[t] || 0) + 1;
      out.push({
        type: t || "UNK",
        ref: `${t || "UNK"}${counters[t] || 1}`,
        match,
      });
    });
  });

  return out;
};

// reasonsByRef supports: { SAN1: ["..."] } OR { SAN1: { reasons: ["..."] } }
const normalizeReasons = (reasons) => {
  if (!Array.isArray(reasons)) return [];
  if (typeof reasons[0] === "string")
    return reasons.map((s) => String(s).trim()).filter(Boolean);

  return reasons
    .flatMap((r) => r?.details ?? [])
    .map((s) => String(s).trim())
    .filter(Boolean);
};

const getReasonsForRef = (reasonsByRef, ref) => {
  const entry = reasonsByRef?.[ref];
  if (Array.isArray(entry)) return normalizeReasons(entry);
  if (Array.isArray(entry?.reasons)) return normalizeReasons(entry.reasons);
  return [];
};

const getRcaDetailsForRef = (reasonsByRef, ref) => {
  const entry = reasonsByRef?.[ref];
  return typeof entry?.rcaDetails === "string" ? entry.rcaDetails.trim() : "";
};

const bullet = (text, i) => (
  <View key={i} style={pepSanPdfStyles.bulletRow}>
    <Text
      style={{
        ...amlReportStyles.amlTextCaption,
        ...pepSanPdfStyles.bulletDot,
      }}
    >
      •
    </Text>
    <Text style={amlReportStyles.amlTextCaption}>{safe(text)}</Text>
  </View>
);

// ---------------- UI-consistent status + tint ----------------
// Match UI logic:
// - unresolved (NO reasons) = white background (NOT red)
// - refer = warning tint
// - not-my-client = success tint
const getReasonStatus = (hasReasons, isMyClientReason) => {
  if (!hasReasons) return "unresolved";
  return isMyClientReason ? "referred" : "not-my-client";
};

const getTintForStatus = (status) => {
  //  UI: no reasons => WHITE background, subtle border, neutral pill
  if (status === "unresolved") {
    return {
      border: theme.colors.Warning,
      bg: theme.colors.Yellow400,
      pillBg: theme.colors.Warning,
      pillBorder: theme.colors.Warning,
      pillText: theme.colors.White,
    };
  }

  //  UI: refer to compliance => warning
  if (status === "referred") {
    return {
      border: theme.colors.Error,
      bg: theme.colors.Red400,
      pillBg: theme.colors.Error,
      pillBorder: theme.colors.Error,
      pillText: theme.colors.White,
    };
  }

  //  UI: not my client => success
  return {
    border: theme.colors.Success,
    bg: theme.colors.Green400,
    pillBg: theme.colors.Success,
    pillBorder: theme.colors.Success,
    pillText: theme.colors.White,
  };
};

const prettifyKey = (k) =>
  String(k || "")
    .replace(/_/g, " ")
    .replace(/([a-z])([A-Z])/g, "$1 $2")
    .trim()
    .replace(/\b\w/g, (c) => c.toUpperCase());

const objectToPairs = (obj) => {
  if (!obj || typeof obj !== "object") return [];
  return Object.entries(obj)
    .filter(
      ([k, v]) => k && v !== null && v !== undefined && String(v).trim() !== "",
    )
    .map(([k, v]) => ({ key: String(k), value: v }));
};

// ---------------- component ----------------
const PepSanctionsPDF = ({
  check,
  checkType,
  response,
  reasonsByRef = {},
  state,
}) => {
  const element = pickPepSanElement(response);
  const results =
    element?.otherData?.response?.searchResults?.[0]?.results || [];
  const cards = buildCards(results);

  const presentTypesSet = new Set(cards.map((c) => c.type));
  const orderedTypes = sortTypes(inferAvailableTypes(element)).filter((t) =>
    presentTypesSet.has(t),
  );

  // Decision override rule (same as UI): all reasons present => Approved
  const rawDecision = getDecisionForService(
    response,
    experianServiceNames.PEP_SANCTIONS,
  );
  const providerLower = rawDecision ? String(rawDecision).toLowerCase() : null;

  const resolved = getAllMatchesResolved(cards, (ref) =>
    getReasonsForRef(reasonsByRef, ref),
  );

  const anyCardIsMyClient = anyMatchIsMyClient(
    cards,
    (ref) => getReasonsForRef(reasonsByRef, ref),
    MY_CLIENT_REASON,
  );

  const uiDecisionLower = resolved
    ? anyCardIsMyClient
      ? "referred"
      : "approved"
    : providerLower;

  const uiDecision = uiDecisionLower
    ? capitalizeFirst(uiDecisionLower)
    : "Not Started";

  const meta = getIconAndColorAndDecision((uiDecision || "").toLowerCase());
  if (!meta) return null;

  const { icon, color, decision } = {
    icon: meta.icon,
    color: meta.color,
    decision: uiDecision,
  };

  return (
    <View
      style={amlReportStyles.section}
      break={checkType !== "PEP & Sanctions"}
    >
      {/* Section header */}
      <View style={amlReportStyles.headingRow}>
        <Text style={amlReportStyles.amlTextHeading}>PEP &amp; Sanctions</Text>

        <View style={{ flexDirection: "row", alignItems: "center", gap: 8 }}>
          {icon}
          <Text style={{ ...amlReportStyles.amlDecisionTextSubheading, color }}>
            {decision}
          </Text>
        </View>
      </View>

      {cards.length === 0 && (
        <View style={{ ...amlReportStyles.successContainer }}>
          <Text
            style={{
              ...amlReportStyles.amlTextCaption,
            }}
          >
            No PEP or Sanctions records found.
          </Text>
        </View>
      )}
      {/* <Text
          style={{
            ...amlReportStyles.amlTextCaption,
            marginTop: 10,
          }}
        >
          No PEP or Sanctions record found.
        </Text> */}

      {/* SAN → PEP → RCA groups */}
      {orderedTypes.map((type, typeIdx) => {
        const cardsForType = cards.filter((c) => c.type === type);
        if (!cardsForType.length) return null;

        return (
          <View key={type} style={pepSanPdfStyles.group} break ={typeIdx !== 0}>
            <View style={pepSanPdfStyles.typeHeadingRow}>
              <Text style={amlReportStyles.amlTextSubheading}>
                {TYPE_LABELS[type]?.label ?? type}
              </Text>
            </View>

            {cardsForType.map((c, cardIdx) => {
              const record = c.match?.record || {};
              const profileId = record?.profileId ?? "no-profile";
              const countryPairs = objectToPairs(record?.countryDetails);

              const names = Array.isArray(record?.names) ? record.names : [];
              const primary = pickPrimaryName(names);
              const displayName = joinName(primary);
              const criteria = criteriaForType(c.match, type);
              const { regionsText, relationshipsText, relatedToIdsText } =
                getCriteriaExtras(criteria);

              const reasonStrings = getReasonsForRef(reasonsByRef, c.ref);
              const rcaDetails = getRcaDetailsForRef(reasonsByRef, c.ref);
              const isRCA = String(type || "").toUpperCase() === "RCA";
              const hasReasons = reasonStrings.length > 0;

              const isMyClientReason = reasonStrings.some((r) =>
                MY_CLIENT_REASON.some((x) => normTxt(x) === normTxt(r)),
              );

              const reasonStatus = getReasonStatus(
                hasReasons,
                isMyClientReason,
              );
              const tint = getTintForStatus(reasonStatus);

              const descriptions = Array.isArray(record?.descriptions)
                ? record.descriptions
                : [];
              const roles = Array.isArray(record?.roles) ? record.roles : [];
              const sanctions = Array.isArray(record?.sanctions)
                ? record.sanctions
                : [];

              // Outcome chip matches UI wording
              const outcomeText = !hasReasons
                ? ""
                : isMyClientReason
                  ? "Refer to Compliance"
                  : isRCA
                    ? "Not a match"
                    : "Not my client";

              const outcomeStyle = !hasReasons
                ? pepSanPdfStyles.outcomeUnresolved
                : isMyClientReason
                  ? pepSanPdfStyles.outcomeRefer
                  : pepSanPdfStyles.outcomeNotClient;

              const outcomeTextColor = !hasReasons
                ? theme.colors.Error
                : isMyClientReason
                  ? theme.colors.Error
                  : theme.colors.Success;

              return (
                <View
                  key={`${c.ref}-${profileId}`}
                  style={[
                    pepSanPdfStyles.card,
                    { borderColor: tint.border, backgroundColor: tint.bg },
                  ]}
                  break={cardIdx !== 0}
                >
                  <View style={pepSanPdfStyles.cardHeader}>
                    <View style={pepSanPdfStyles.headerLeft}>
                      {/* Ref pill */}
                      <View
                        style={[
                          pepSanPdfStyles.refPill,
                          {
                            borderColor: tint.pillBorder,
                            backgroundColor: tint.pillBg,
                          },
                        ]}
                      >
                        <Text
                          style={{
                            ...amlReportStyles.amlTextValue,
                            color: tint.pillText, //  match tint
                          }}
                        >
                          {c.ref}
                        </Text>
                      </View>

                      <Text style={amlReportStyles.amlTextHeading}>
                        {String(type || "").toUpperCase() === "RCA" ? (
                          <>
                            <View style={pepSanPdfStyles.metaItem}>
                              <Text style={amlReportStyles.amlTextCaption}>
                                <Text style={amlReportStyles.amlTextSubheading}>
                                  {safe(relationshipsText)}
                                </Text>{" "}
                                (Relationship to{" "}
                                <Text style={amlReportStyles.amlTextValue}>
                                  {displayName}
                                </Text>
                                )
                              </Text>
                              {/* <Text style={amlReportStyles.amlTextValue}>
                                {safe(relationshipsText)}{" "}
                                <Text style={amlReportStyles.amlTextCaption}>
                                  (Relationship to:{displayName})
                                </Text>
                              </Text> */}
                            </View>

                            {/* <View style={pepSanPdfStyles.metaItem}>
                                <Text style={amlReportStyles.amlTextCaption}>
                                  <Text style={amlReportStyles.amlTextValue}>
                                    Related To ID:
                                  </Text>{" "}
                                  {safe(relatedToIdsText)}
                                </Text>
                              </View> */}
                          </>
                        ) : (
                          <>{displayName} </>
                        )}
                      </Text>
                    </View>

                    {/* Outcome */}
                    <View style={pepSanPdfStyles.headerRight}>
                      <View style={[outcomeStyle]}>
                        <Text
                          style={{
                            ...amlReportStyles.amlTextSubheading,
                            color: outcomeTextColor,
                          }}
                        >
                          {outcomeText}
                        </Text>
                      </View>

                      {!hasReasons && (
                        <Text
                          style={{
                            ...amlReportStyles.amlTextSubheading,
                            color: theme.colors.Warning,
                          }}
                        >
                          Refer to Compliance
                        </Text>
                      )}
                    </View>
                  </View>

                  <View style={pepSanPdfStyles.metaGrid}>
                    <View style={pepSanPdfStyles.metaItem}>
                      <Text style={amlReportStyles.amlTextCaption}>
                        <Text style={amlReportStyles.amlTextValue}>
                          Region:
                        </Text>{" "}
                        {safe(regionsText)}
                      </Text>
                    </View>

                    <View style={pepSanPdfStyles.metaItem}>
                      <Text style={amlReportStyles.amlTextCaption}>
                        <Text style={amlReportStyles.amlTextValue}>DOB:</Text>{" "}
                        {fmtDate(record?.dateOfBirth)}
                      </Text>
                    </View>

                    <View style={pepSanPdfStyles.metaItem}>
                      <Text style={amlReportStyles.amlTextCaption}>
                        <Text style={amlReportStyles.amlTextValue}>
                          Status:
                        </Text>{" "}
                        {safe(record?.activeStatus)}
                      </Text>
                    </View>

                    <View style={pepSanPdfStyles.metaItem}>
                      <Text style={amlReportStyles.amlTextCaption}>
                        <Text style={amlReportStyles.amlTextValue}>
                          Deceased:
                        </Text>{" "}
                        {record?.deceased === null ||
                        record?.deceased === undefined
                          ? "-"
                          : capitalizeFirst(String(record.deceased))}
                      </Text>
                    </View>

                    <View style={pepSanPdfStyles.metaItem}>
                      <Text style={amlReportStyles.amlTextCaption}>
                        <Text style={amlReportStyles.amlTextValue}>
                          Gender:
                        </Text>{" "}
                        {capitalizeFirst(safe(record?.gender))}
                      </Text>
                    </View>
                  </View>

                  {!!descriptions.length && (
                    <View style={pepSanPdfStyles.block}>
                      <View style={pepSanPdfStyles.blockTitleRow}>
                        <Text style={amlReportStyles.amlTextSubheading}>
                          Descriptions
                        </Text>
                      </View>
                      <View style={pepSanPdfStyles.bulletList}>
                        {descriptions.map((d, i) =>
                          bullet(
                            `${safe(d?.recordType)}: ${safe(d?.description)}`,
                            i,
                          ),
                        )}
                      </View>
                    </View>
                  )}

                  {!!roles.length && (
                    <View style={pepSanPdfStyles.block}>
                      <Text style={amlReportStyles.amlTextSubheading}>
                        Roles
                      </Text>
                      <View style={pepSanPdfStyles.bulletList}>
                        {roles.map((r, i) =>
                          bullet(
                            `${safe(r?.type)}: ${safe(r?.title)}${
                              r?.category ? ` (${r.category})` : ""
                            }`,
                            i,
                          ),
                        )}
                      </View>
                    </View>
                  )}

                  {hasReasons && (
                    <View style={pepSanPdfStyles.block}>
                      <Text style={amlReportStyles.amlTextSubheading}>
                        Reasons
                      </Text>
                      <View style={pepSanPdfStyles.bulletList}>
                        {reasonStrings.map((r, i) => bullet(r, i))}
                      </View>

                      {isRCA && !!rcaDetails && (
                        <View style={pepSanPdfStyles.chipWrap}>
                          <View style={pepSanPdfStyles.chip}>
                            <Text style={amlReportStyles.amlTextCaption}>
                              Details: {safe(rcaDetails)}
                            </Text>
                          </View>
                        </View>
                      )}
                    </View>
                  )}

                  <View style={pepSanPdfStyles.block}>
                    <Text style={amlReportStyles.amlTextSubheading}>
                      Names / Aliases
                    </Text>
                    {names.length ? (
                      <View style={pepSanPdfStyles.chipWrap}>
                        {names.map((n, i) => (
                          <View key={i} style={pepSanPdfStyles.chip}>
                            <Text style={amlReportStyles.amlTextCaption}>
                              {safe(n?.type)}: {safe(n?.firstName)}{" "}
                              {safe(n?.surname)}
                            </Text>
                          </View>
                        ))}
                      </View>
                    ) : (
                      <Text
                        style={{
                          ...amlReportStyles.amlTextCaption,
                          ...pepSanPdfStyles.muted,
                        }}
                      >
                        No names listed.
                      </Text>
                    )}
                  </View>

                  {!!countryPairs.length && (
                    <View style={pepSanPdfStyles.block}>
                      <Text style={amlReportStyles.amlTextSubheading}>
                        Country Details
                      </Text>
                      <View style={pepSanPdfStyles.chipWrap}>
                        {countryPairs.map((p) => (
                          <View key={p.key} style={pepSanPdfStyles.chip}>
                            <Text style={amlReportStyles.amlTextCaption}>
                              {prettifyKey(p.key)}: {safe(p.value)}
                            </Text>
                          </View>
                        ))}
                      </View>
                    </View>
                  )}

                  <View style={pepSanPdfStyles.block}>
                    {sanctions.length ? (
                      <View>
                        <Text style={amlReportStyles.amlTextSubheading}>
                          Sanctions
                        </Text>
                        <View style={{ gap: 6 }}>
                          {sanctions.map((s, i) => (
                            <View key={i} style={pepSanPdfStyles.sanctionCard}>
                              <Text style={amlReportStyles.amlTextCaption}>
                                <Text style={amlReportStyles.amlTextValue}>
                                  {safe(s?.name)}
                                </Text>
                              </Text>

                              <Text
                                style={{
                                  ...amlReportStyles.amlTextCaption,
                                  ...pepSanPdfStyles.muted,
                                }}
                              >
                                {safe(s?.region)} • {safe(s?.status)} •{" "}
                                {safe(s?.listProviderCode)} • Code{" "}
                                {safe(s?.code)}
                              </Text>

                              <Text
                                style={{
                                  ...amlReportStyles.amlTextCaption,
                                  ...pepSanPdfStyles.muted,
                                }}
                              >
                                Since: {safe(s?.sinceDay, "")}{" "}
                                {safe(s?.sinceMonth, "")} {safe(s?.sinceYear)}
                                {s?.toMonth || s?.toYear || s?.toDay ? (
                                  <>
                                    {" "}
                                    • To: {safe(s?.toDay, "")}{" "}
                                    {safe(s?.toMonth, "")} {safe(s?.toYear, "")}
                                  </>
                                ) : null}
                              </Text>
                            </View>
                          ))}
                        </View>
                      </View>
                    ) : null}
                  </View>
                </View>
              );
            })}
          </View>
        );
      })}
    </View>
  );
};

export default PepSanctionsPDF;
